import { MgpPayload, MgpStepNames, MGPWizard } from "@earned/wizard";
import * as React from "react";
import useTasksStore from "@modules/tasks/state";
import { WizardId } from "@modules/tasks/types";
import { useMgpInfo } from "@modules/mgp/queries";
import { useUpdateMgpInfo } from "@modules/mgp/mutations";
import { useMgpStore } from "@modules/mgp/state";
import { merge, isEmpty } from "lodash";
import { useWizardCloseConfirm } from "@modules/tasks/hooks.web";

const MGPWizardController: React.FC = () => {
  const {
    wizardId,
    setWizardId,
    isWizardOpen,
    setIsWizardOpen,
    wizardTask,
    setWizardTask,
    setRemoteCompleteTaskId,
  } = useTasksStore();

  const { fields: cache, navHistory, setNavHistory, setField } = useMgpStore();
  const { data, isLoading } = useMgpInfo();
  const {
    mutateAsync,
    isLoading: isSubmitting,
    isSuccess,
  } = useUpdateMgpInfo();

  const { openCloseConfirmDialog, CloseConfirmDialogComponent } =
    useWizardCloseConfirm();

  const isOpen = React.useMemo(
    () => wizardId === WizardId.MONEY_GUIDE_PRO && isWizardOpen,
    [wizardId, isWizardOpen]
  );

  const mgpInfoCache = React.useMemo(
    () => merge({}, data, cache),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const handlePageChange = React.useCallback(
    (page: MgpStepNames, navHistory: MgpStepNames[]) => {
      setNavHistory(navHistory);
    },
    [setNavHistory]
  );

  const handleSubmit = React.useCallback(
    async (payload: MgpPayload) => {
      if (isEmpty(payload.coClient) || !payload["coClient"]?.CLIENT_ID) {
        delete payload.coClient;
      }
      await mutateAsync({ data: payload });
    },
    [mutateAsync]
  );

  React.useEffect(() => {
    if (isSuccess) {
      setNavHistory([]);
    }
  }, [isSuccess, setNavHistory]);

  const handleDone = React.useCallback(async () => {
    if (wizardTask) setRemoteCompleteTaskId(wizardTask.id);
    setIsWizardOpen(false);
    setWizardTask(null);
    setWizardId(null);
  }, [
    setRemoteCompleteTaskId,
    setWizardId,
    setWizardTask,
    wizardTask,
    setIsWizardOpen,
  ]);

  return (
    <>
      <MGPWizard
        isOpen={isOpen}
        close={openCloseConfirmDialog}
        onDone={handleDone}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        initialNavHistory={navHistory}
        cache={mgpInfoCache}
        isLoading={isLoading}
        isSuccess={isSuccess}
        onPageChange={handlePageChange}
        onFieldChange={(config) =>
          setField({ key: config.key as keyof MgpPayload, value: config.field })
        }
      />
      <CloseConfirmDialogComponent />
    </>
  );
};

export default MGPWizardController;
